/* eslint-disable react/jsx-props-no-spreading */
import React, { memo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useApiGet } from "../api/general-service";

/**
 * A form field component for handling features and sub-features, styled with Tailwind CSS.
 * It uses react-hook-form's useFieldArray for dynamic field management.
 */
const Features = () => {
  const { data: featuresData, isLoading } = useApiGet({
    endpoint: `/feature`,
    queryKey: ["feature"],
    config: {
      select: (data) => {
        return data?.data?.data;
      },
    },
  });

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext(); // Access form context

  const watchFieldArray = watch("features");
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "features",
  });

  // Combine field metadata and watched values for each dependent
  const featuresList = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray?.[index],
    };
  });

  const handleFeatureChange = (index, value) => {
    // Find the selected feature's sub-features
    const selectedFeature = featuresData.find((item) => item.id == value);
    const subFeatures = selectedFeature?.feature_detail || [];

    console.log({ subFeatures, selectedFeature, featuresData });

    // Update the `sub_features` in the field array for this specific row
    update(index, {
      ...featuresList[index],
      id: value, // Update the id of the feature in this row
      sub_features: [], // Reset sub-features selection whenever feature changes
      sub_feature_options: subFeatures,
    });
  };

  if (isLoading) return "loading...";

  return (
    <div className="mt-4">
      <h4 className="mb-2 text-2xl font-bold">Features</h4>

      {featuresList?.map((feature, index) => {
        return (
          <div key={feature.id} id="features" className="mt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Feature Dropdown */}
              <div className="col-span-2">
                <label
                  htmlFor={`features[${index}].id`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Select Feature
                </label>
                <Controller
                  name={`features[${index}].id`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <select
                      id={`features[${index}].id`}
                      className={`w-full rounded-md border p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${
                        errors.features?.[index]?.id?.message
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={value || ""}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log({ newValue, value });
                        onChange(newValue); // Update the form field
                        handleFeatureChange(index, newValue); // Update local sub-features
                      }}
                    >
                      <option value="">Select a feature</option>
                      {featuresData?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.heading}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.features?.[index]?.id?.message && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.features?.[index]?.id?.message}
                  </p>
                )}
              </div>
              {/* Sub Features Checkboxes */}
              <div className="col-span-2">
                <label
                  htmlFor={`features[${index}].sub_features`}
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Sub-Features
                </label>
                <Controller
                  name={`features[${index}].sub_features`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <div id={`features[${index}].sub_features`}>
                      <CheckboxGroup
                        options={feature.sub_feature_options || []}
                        value={value || []}
                        onChange={onChange}
                      />
                      {errors.features?.[index]?.sub_features?.message && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.features?.[index]?.sub_features?.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            {featuresList?.length > 1 && (
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  className="rounded-md border border-solid border-red-500 bg-white px-4 py-2 text-sm font-medium text-red-500 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  onClick={() => remove(index)}
                >
                  Remove Feature
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="mt-2 rounded-md border border-solid border-red-500 bg-white px-4 py-2 text-sm font-medium text-red-500 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        onClick={() => {
          append({
            id: "",
            sub_features: [],
            sub_feature_options: [],
          });
        }}
      >
        Add more feature
      </button>
    </div>
  );
};

export default memo(Features);

const CheckboxGroup = ({ options, value = [], onChange }) => {
  console.log({ options, value });
  const handleChange = (event) => {
    const { name, checked } = event.target;
    let updatedValues = [...value];
    const numericName = Number(name);
    if (checked) {
      updatedValues.push(numericName);
    } else {
      updatedValues = updatedValues.filter((item) => item !== numericName);
    }
    onChange(updatedValues);
  };

  return (
    <div className="flex flex-wrap gap-4 pt-2">
      {options.map((option) => (
        <label key={option.id} className="inline-flex items-center space-x-2">
          <input
            type="checkbox"
            className="form-checkbox h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={value.includes(option.id)}
            onChange={handleChange}
            name={String(option.id)}
          />
          <span className="text-gray-700">{option.sub_heading}</span>
        </label>
      ))}
    </div>
  );
};
